import React from 'react';
import { motion } from "framer-motion";
import { Button } from "@/shared/components/ui/button";
import { Card, CardContent } from "@/shared/components/ui/card";
import { CheckCircle2, ArrowRight } from "lucide-react";

const Pricing = () => {
  return (
    <div className="pt-32 pb-20 px-4">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800/50 border border-slate-700/50 mb-8">
            <span className="text-sm text-slate-400">Simple, Transparent Pricing</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            Plans that scale with <br />
            <span className="gradient-text">your business</span>
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Whether you're a straightforward merchant or an enterprise engine, we have a plan built for your needs.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {/* Starter Plan */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            <Card className="bg-slate-900/50 border-slate-800/50 hover:border-slate-700 transition-all duration-300 h-full flex flex-col pt-6">
              <CardContent className="p-8 flex-1 flex flex-col">
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-white mb-2">Merchant</h3>
                  <p className="text-slate-400 text-sm">Perfect for growing businesses</p>
                </div>
                <div className="mb-6">
                  <span className="text-5xl font-bold text-white">2.9%</span>
                  <span className="text-slate-400 text-xl font-medium"> + 30¢</span>
                  <p className="text-slate-500 text-sm mt-3">per successful transaction</p>
                </div>
                <ul className="space-y-4 mb-8 flex-1">
                  {["All global payment methods", "Standard fraud protection", "Next-day payouts", "Basic reporting"].map((feature, i) => (
                    <li key={i} className="flex items-center gap-3 text-slate-300 text-sm">
                      <CheckCircle2 className="h-5 w-5 text-blue-400 shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button className="w-full bg-slate-800 hover:bg-slate-700 text-white mt-auto py-6">Get Started</Button>
              </CardContent>
            </Card>
          </motion.div>

          {/* Pro Plan */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
          >
            <Card className="bg-slate-800/50 border-blue-500/30 hover:border-blue-500/50 transition-all duration-300 h-full relative flex flex-col card-glow pt-6">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-gradient-to-r from-blue-500 to-violet-500 text-white px-6 py-1.5 rounded-full text-sm font-semibold shadow-lg">
                Most Popular
              </div>
              <CardContent className="p-8 flex-1 flex flex-col">
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-white mb-2">Reseller</h3>
                  <p className="text-slate-400 text-sm">For payment service providers</p>
                </div>
                <div className="mb-6">
                  <span className="text-5xl font-bold text-white">Custom</span>
                  <p className="text-slate-500 text-sm mt-3">tailored volume pricing</p>
                </div>
                <ul className="space-y-4 mb-8 flex-1">
                  {["White-label dashboard", "Advanced fraud management", "Custom payout schedules", "Dedicated account manager", "API access & webhooks"].map((feature, i) => (
                    <li key={i} className="flex items-center gap-3 text-slate-300 text-sm">
                      <CheckCircle2 className="h-5 w-5 text-violet-400 shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button className="w-full bg-gradient-to-r from-blue-500 to-violet-600 hover:from-blue-600 hover:to-violet-700 text-white mt-auto py-6">
                  Contact Sales
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>
          </motion.div>

          {/* Enterprise Plan */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.3 }}
          >
            <Card className="bg-slate-900/50 border-slate-800/50 hover:border-slate-700 transition-all duration-300 h-full flex flex-col pt-6">
              <CardContent className="p-8 flex-1 flex flex-col">
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-white mb-2">Engine</h3>
                  <p className="text-slate-400 text-sm">Enterprise infrastructure</p>
                </div>
                <div className="mb-6">
                  <span className="text-5xl font-bold text-white">Scale</span>
                  <p className="text-slate-500 text-sm mt-3">unlimited processing</p>
                </div>
                <ul className="space-y-4 mb-8 flex-1">
                  {["Multi-provider routing", "99.999% uptime SLA", "Custom smart routing rules", "Priority 24/7 technical support", "On-premise deployment options"].map((feature, i) => (
                    <li key={i} className="flex items-center gap-3 text-slate-300 text-sm">
                      <CheckCircle2 className="h-5 w-5 text-emerald-400 shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button className="w-full bg-slate-800 hover:bg-slate-700 text-white mt-auto py-6">Contact Sales</Button>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  )
}

export default Pricing;